import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import SaveBackdrop from '../../../components/saveBackdrop';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadEnlistment, checkUserAuthenticated,
  getEnlistmentParentAccountSearch, goToRoutePage
} from '../../../utils/helper';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel,
  TextField, Typography
} from '../../../utils/material-ui-core';

interface ConfirmProps {
  locationItemKey: string;
  open: boolean;
  callActionButton: (confirmOutput: ConfirmOutput) => void;
}

const formDefaultValue = {
  accountNumber: "",
  name: ""
};

const formDefaultTouchValue = {
  accountNumber: true,
  name: true
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  accountNumber: Yup.string().trim().required("Account name is required"),
  name: Yup.string().trim().required("Name is required")
});

const CreateParentAccount = ({ locationItemKey, open, callActionButton }: ConfirmProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, userFunctionAccess, showAlert } = useContext(AppContext);

  const formReference = useRef(null) as any;

  const [initialFormValues, setInitialFormValues] = useState(formDefaultValue);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  // Upload file for validation
  const validateAccountNumber = async () => {
    formReference.current.validateForm();

    if (!formReference.current.isValid) {
      return;
    }

    setSaveInProgress(true);

    const apiResponse = await getEnlistmentParentAccountSearch(locationItemKey, formReference.current.values.accountNumber);

    if (apiResponse.success) {

      const filterParentAccounts = apiResponse.data.parentAccounts.filter(account => account.accountNumber === formReference.current.values.accountNumber)

      if (filterParentAccounts.length > 0) {
        showAlert({ message: "Account number already exists", severity: "error" });
        formReference.current.setFieldError("accountNumber", "Account number already exists");
      }
      else {
        save();
      }

    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
    }

    setSaveInProgress(false);
  };

  const save = async () => {

    const payload = {
      accountNumber: formReference.current.values.accountNumber,
      name: formReference.current.values.name
    }

    try {
      setSaveInProgress(true);
      await API.post("/api/enlistmentadmin/locations/" + locationItemKey + "/createparentaccount", payload)
        .then(async (response) => {
          showAlert({ message: "Successfully parent account created.", severity: "success" });
          callActionButton({ status: "refresh", data: response.data.newLocationItemKey });
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to create parent account."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  }

  // Reset input file control and other variables
  const reset = () => {
    setSaveInProgress(false);
    setInitialFormValues(formDefaultValue);
  };

  // Cancel upload file
  const cancel = () => {
    callActionButton({ status: "cancel", data: null });
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (!canReadEnlistment(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <Dialog className={globalStyles.dialogContainer} fullWidth={true} open={open} maxWidth={"sm"}>
      <DialogTitle className={globalStyles.dialogTitleContainer}>
        <Typography variant="subtitle1" component="div">
          Create Parent Account
        </Typography>
      </DialogTitle>

      <DialogContent dividers className={globalStyles.formikContainer}>
        <SaveBackdrop open={saveInProgress} />

        <Box className={pageStyles.root}>
          <Formik
            innerRef={formReference}
            initialValues={initialFormValues}
            validationSchema={formValidationSchema}
            validateOnMount={true}
            initialTouched={formDefaultTouchValue}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={validateAccountNumber}>
            {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue }) => (
              <Form>
                <Grid container spacing={3} className={globalStyles.bottomSpacer}>
                  <Grid item xs={12}>
                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                      <InputLabel shrink={true}>
                        Account Number
                      </InputLabel>
                      <br />
                      <TextField
                        name="accountNumber"
                        type="input"
                        id="accountNumber"
                        value={values.accountNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.accountNumber)}
                        helperText={errors.accountNumber ? errors.accountNumber : null}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                      <InputLabel shrink={true}>
                        Name
                      </InputLabel>
                      <br />
                      <TextField
                        name="name"
                        type="input"
                        id="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.name)}
                        helperText={errors.name ? errors.name : null}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                <Box p={1}>&nbsp;</Box>
              </Form>
            )}
          </Formik>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" variant="contained" onClick={validateAccountNumber} className={globalStyles.buttonDefault}>
          Create
        </Button>
        &nbsp;
        <Button onClick={cancel} autoFocus className={globalStyles.buttonDefault}>
          <u>Cancel</u>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    errormessage: {
      color: theme.palette.error.main,
    },
  })
);

export default CreateParentAccount;
